import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Send, Download } from "lucide-react";
import { toast } from "sonner";
import {
  useAndesEmitCertificate,
  useAndesPullPem,
  useArtifacts,
  useCertificateRequests,
  useCreateCertificateRequest,
  useSetCertificateValidity,
  useTransitionCertificateRequest,
} from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Field, Input, Select } from "@/components/ui/Input";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { StatusBadge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";
import {
  REQUEST_TRANSITIONS,
  type AndesEmitResponseData,
  type AndesPullPemResponseData,
  type Artifact,
  type ArtifactType,
  type CertificateClass,
  type CertificateRequest,
  type CertificateType,
  type CreateCertificateRequestInput,
  type PersonType,
  type RequestStatus,
  type TransitionCertificateRequestInput,
  type ValidityYears,
} from "@/types";

/** Must stay in sync with packages/andesbridger-certificates required-soporte.ts (PC-55). */
const REQUIRED_SOPORTE: Record<PersonType, ArtifactType[]> = {
  natural: ["cedula"],
  juridica: ["cedula", "rut", "camara_comercio"],
};

const SOPORTE_CHECKLIST_LABELS: Record<string, string> = {
  cedula: "Cédula",
  rut: "RUT",
  camara_comercio: "Cámara de comercio",
  csr: "CSR",
};

function isPresentSoporte(artifacts: Artifact[], type: ArtifactType): boolean {
  if (type === "csr") return artifacts.some((a) => a.type === "csr");
  return artifacts.some((a) => a.type === type && !!a.fileName && !!a.fileUrl && !a.encrypted);
}

const EMPTY: CreateCertificateRequestInput = {
  type: "firma_digital",
  class: "II",
  validityYears: 1,
};

const CERT_TYPES: { value: CertificateType; label: string }[] = [
  { value: "factura_electronica", label: "Factura electrónica" },
  { value: "firma_digital", label: "Firma digital" },
  { value: "ssl", label: "SSL" },
  { value: "autenticacion", label: "Autenticación" },
];

/** Validity in months (PC-35) — falls back to the legacy years column. */
function validityLabel(r: CertificateRequest): string {
  const months = r.validityMonths ?? r.validityYears * 12;
  if (months < 12) return `${months} meses`;
  const years = Math.round(months / 12);
  return years === 1 ? "1 año" : `${years} años`;
}

interface CertificateRequestsTabProps {
  subscriberId: string;
  documentNumber: string;
  subscriberType: PersonType;
}

export function CertificateRequestsTab({
  subscriberId,
  documentNumber,
  subscriberType,
}: CertificateRequestsTabProps) {
  const list = useCertificateRequests(subscriberId);
  const create = useCreateCertificateRequest(subscriberId);
  const transition = useTransitionCertificateRequest(subscriberId);
  const setValidity = useSetCertificateValidity(subscriberId);
  const andesEmit = useAndesEmitCertificate(subscriberId);
  const andesPullPem = useAndesPullPem(subscriberId);
  const artifacts = useArtifacts(subscriberId);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateCertificateRequestInput>(EMPTY);
  const [transitioning, setTransitioning] = useState<{
    request: CertificateRequest;
    target: Exclude<RequestStatus, "pending">;
  } | null>(null);
  const [transitionInput, setTransitionInput] = useState<TransitionCertificateRequestInput>({
    status: "verified",
  });

  // Validity (start/end dates) editing state
  const [validityFor, setValidityFor] = useState<CertificateRequest | null>(null);
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");

  // Andes emission state
  const [emitOpen, setEmitOpen] = useState(false);
  const [emitDryRun, setEmitDryRun] = useState(true);
  const [emitResult, setEmitResult] = useState<AndesEmitResponseData | null>(null);
  const [emitError, setEmitError] = useState<{
    code: string;
    message: string;
    attemptId?: string;
    confirmTitle?: string;
    confirmBody?: string;
    screenshots?: AndesEmitResponseData["screenshots"];
  } | null>(null);

  const [pullOpen, setPullOpen] = useState(false);
  const [pullDryRun, setPullDryRun] = useState(true);
  const [pullResult, setPullResult] = useState<AndesPullPemResponseData | null>(null);
  const [pullError, setPullError] = useState<{
    code: string;
    message: string;
    pullId?: string;
    screenshots?: AndesPullPemResponseData["screenshots"];
  } | null>(null);

  function setF<K extends keyof CreateCertificateRequestInput>(
    key: K,
    value: CreateCertificateRequestInput[K]
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync(form);
      toast.success("Certificate request created");
      setCreateOpen(false);
      setForm(EMPTY);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to create request");
    }
  }

  function openTransition(request: CertificateRequest, target: Exclude<RequestStatus, "pending">) {
    setTransitioning({ request, target });
    setTransitionInput({ status: target });
  }

  async function onTransition(e: React.FormEvent) {
    e.preventDefault();
    if (!transitioning) return;
    try {
      await transition.mutateAsync({
        reqId: transitioning.request.id,
        input: transitionInput,
      });
      toast.success(`Marked as ${transitioning.target}`);
      setTransitioning(null);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to transition");
    }
  }

  function openValidity(request: CertificateRequest) {
    setValidityFor(request);
    setValidFrom(request.validFrom ?? "");
    setValidUntil(request.validUntil ?? "");
  }

  async function onSaveValidity(e: React.FormEvent) {
    e.preventDefault();
    if (!validityFor) return;
    try {
      await setValidity.mutateAsync({
        reqId: validityFor.id,
        validFrom: validFrom || null,
        validUntil: validUntil || null,
      });
      toast.success("Vigencia actualizada");
      setValidityFor(null);
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to set validity");
    }
  }

  function openEmit() {
    setEmitResult(null);
    setEmitError(null);
    setEmitDryRun(true);
    setEmitOpen(true);
  }

  const emitChecklist = useMemo(() => {
    const items: ArtifactType[] = [...REQUIRED_SOPORTE[subscriberType], "csr"];
    const present = artifacts.data ?? [];
    return items.map((type) => ({
      type,
      label: SOPORTE_CHECKLIST_LABELS[type] ?? type,
      ok: isPresentSoporte(present, type),
    }));
  }, [artifacts.data, subscriberType]);

  const missingSoporte = emitChecklist.filter((item) => !item.ok).map((item) => item.type);

  function openPullPem() {
    setPullResult(null);
    setPullError(null);
    setPullDryRun(true);
    setPullOpen(true);
  }

  async function onPullPem() {
    try {
      const res = await andesPullPem.mutateAsync({ dryRun: pullDryRun });
      setPullResult(res.data);
      toast.success(
        pullDryRun
          ? "Dry run completed — review the screenshots"
          : res.data.skipped
            ? "PEM/PFX already generated for this emission"
            : "PEM descargado y PFX/P12 generados",
      );
    } catch (err) {
      if (err instanceof ApiError) {
        const details = (err.details ?? {}) as {
          pullId?: string;
          screenshots?: AndesPullPemResponseData["screenshots"];
        };
        setPullError({
          code: err.code,
          message: err.message,
          pullId: details.pullId,
          screenshots: details.screenshots,
        });
        toast.error(`[${err.code}] ${err.message}`);
      } else toast.error("Failed to pull PEM from Andes");
    }
  }

  async function onEmit() {
    if (missingSoporte.length > 0) {
      toast.error(
        `Faltan soportes requeridos: ${missingSoporte.map((t) => SOPORTE_CHECKLIST_LABELS[t] ?? t).join(", ")}`,
      );
      return;
    }
    try {
      const res = await andesEmit.mutateAsync({ dryRun: emitDryRun });
      setEmitResult(res.data);
      toast.success(
        emitDryRun
          ? "Dry run completed — review the screenshots"
          : "Solicitud enviada a Andes",
      );
    } catch (err) {
      if (err instanceof ApiError) {
        const details = (err.details ?? {}) as {
          attemptId?: string;
          confirmTitle?: string;
          confirmBody?: string;
          screenshots?: AndesEmitResponseData["screenshots"];
        };
        setEmitError({
          code: err.code,
          message: err.message,
          attemptId: details.attemptId,
          confirmTitle: details.confirmTitle,
          confirmBody: details.confirmBody,
          screenshots: details.screenshots,
        });
        toast.error(`[${err.code}] ${err.message}`);
      } else toast.error("Failed to emit to Andes");
    }
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex items-center justify-between">
        <CardTitle>Certificate requests</CardTitle>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={openEmit}
            title="Enviar solicitud al panel Andes SCD"
          >
            <Send className="h-3.5 w-3.5" /> Emit to Andes
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={openPullPem}
            title="Descargar PEM emitido y generar PFX/P12"
          >
            <Download className="h-3.5 w-3.5" /> Pull PEM from Andes
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> New request
          </Button>
        </div>
      </CardHeader>
      {list.isPending ? (
        <CenteredSpinner />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.length === 0 ? (
        <EmptyState
          title="No certificate requests"
          description="Create a request to start the issuance workflow."
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Type</TH>
              <TH>Class</TH>
              <TH>Validity</TH>
              <TH>Status</TH>
              <TH>Pago</TH>
              <TH>Serial</TH>
              <TH>Vigencia</TH>
              <TH>Created</TH>
              <TH className="text-right">Transition</TH>
            </tr>
          </THead>
          <TBody>
            {list.data.map((r) => {
              const allowed = REQUEST_TRANSITIONS[r.status];
              return (
                <TR key={r.id}>
                  <TD className="font-medium text-slate-900">{r.type}</TD>
                  <TD>Clase {r.class}</TD>
                  <TD>{validityLabel(r)}</TD>
                  <TD>
                    <StatusBadge status={r.status} />
                  </TD>
                  <TD>
                    {r.paidAt ? (
                      <div className="space-y-0.5">
                        <span
                          className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700"
                          title={`Pagado ${formatDate(r.paidAt)}${
                            r.paidAmountCents != null
                              ? ` · $${Math.round(r.paidAmountCents / 100).toLocaleString("es-CO")} ${r.paidCurrency ?? "COP"}`
                              : ""
                          }`}
                        >
                          Pagado
                        </span>
                        {r.invoice && (
                          <div className="font-mono text-[10px] text-slate-400">{r.invoice.number}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </TD>
                  <TD className="font-mono text-xs">
                    {r.certificateSerial ?? <span className="text-slate-400">—</span>}
                  </TD>
                  <TD>
                    <button
                      type="button"
                      onClick={() => openValidity(r)}
                      className="text-left text-[11px] hover:underline"
                    >
                      {r.validFrom || r.validUntil ? (
                        <span className="text-slate-700">
                          {r.validFrom ?? "—"} → {r.validUntil ?? "—"}
                        </span>
                      ) : (
                        <span className="text-brand-600">+ Fechas</span>
                      )}
                    </button>
                  </TD>
                  <TD className="text-[11px] text-slate-500">{formatDate(r.createdAt)}</TD>
                  <TD className="text-right">
                    {allowed.length === 0 ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        {allowed.map((next) => (
                          <Button
                            key={next}
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              openTransition(r, next as Exclude<RequestStatus, "pending">)
                            }
                          >
                            → {next}
                          </Button>
                        ))}
                      </div>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New certificate request"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button form="req-create-form" type="submit" loading={create.isPending}>
              Create
            </Button>
          </>
        }
      >
        <form id="req-create-form" onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Type" required className="md:col-span-2">
            <Select
              value={form.type}
              onChange={(e) => setF("type", e.target.value as CertificateType)}
            >
              {CERT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Class" required>
            <Select
              value={form.class}
              onChange={(e) => setF("class", e.target.value as CertificateClass)}
            >
              <option value="II">Clase II</option>
              <option value="III">Clase III</option>
            </Select>
          </Field>
          <Field label="Validity" required>
            <Select
              value={form.validityYears}
              onChange={(e) => setF("validityYears", Number(e.target.value) as ValidityYears)}
            >
              <option value={1}>1 year</option>
              <option value={2}>2 years</option>
              <option value={3}>3 years</option>
            </Select>
          </Field>
        </form>
      </Dialog>

      <Dialog
        open={!!transitioning}
        onClose={() => setTransitioning(null)}
        title={`Transition to ${transitioning?.target ?? ""}`}
        description={
          transitioning
            ? `From ${transitioning.request.status} → ${transitioning.target}`
            : undefined
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setTransitioning(null)}>
              Cancel
            </Button>
            <Button
              form="req-transition-form"
              type="submit"
              variant={transitioning?.target === "rejected" || transitioning?.target === "revoked" ? "danger" : "primary"}
              loading={transition.isPending}
            >
              Confirm
            </Button>
          </>
        }
      >
        {transitioning && (
          <form id="req-transition-form" onSubmit={onTransition} className="space-y-3">
            {transitioning.target === "verified" && (
              <Field label="Verified by" hint="Your name or operator ID">
                <Input
                  value={transitionInput.verifiedBy ?? ""}
                  onChange={(e) =>
                    setTransitionInput((i) => ({ ...i, verifiedBy: e.target.value }))
                  }
                  maxLength={256}
                />
              </Field>
            )}
            {transitioning.target === "issued" && (
              <Field label="Certificate serial" required>
                <Input
                  value={transitionInput.certificateSerial ?? ""}
                  onChange={(e) =>
                    setTransitionInput((i) => ({ ...i, certificateSerial: e.target.value }))
                  }
                  maxLength={100}
                  required
                />
              </Field>
            )}
            {(transitioning.target === "rejected" || transitioning.target === "revoked") && (
              <Field label="Reason" required>
                <Input
                  value={transitionInput.revocationReason ?? ""}
                  onChange={(e) =>
                    setTransitionInput((i) => ({ ...i, revocationReason: e.target.value }))
                  }
                  maxLength={500}
                  required
                />
              </Field>
            )}
            {transitioning.target === "expired" && (
              <p className="text-sm text-slate-600">Mark this certificate as expired.</p>
            )}
          </form>
        )}
      </Dialog>

      <Dialog
        open={!!validityFor}
        onClose={() => setValidityFor(null)}
        title="Vigencia del certificado"
        description={
          validityFor ? `${validityFor.type} · ${validityLabel(validityFor)}` : undefined
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setValidityFor(null)}>
              Cancel
            </Button>
            <Button form="validity-form" type="submit" loading={setValidity.isPending}>
              Guardar
            </Button>
          </>
        }
      >
        {validityFor && (
          <form id="validity-form" onSubmit={onSaveValidity} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Inicio (validFrom)">
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            </Field>
            <Field label="Fin (validUntil)">
              <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </Field>
            <p className="text-xs text-slate-500 sm:col-span-2">
              Deja ambas fechas vacías para limpiar la vigencia.
            </p>
          </form>
        )}
      </Dialog>

      <Dialog
        open={emitOpen}
        onClose={() => setEmitOpen(false)}
        title="Emit to Andes SCD"
        description={`Documento ${documentNumber} (Persona ${subscriberType})`}
        footer={
          <>
            <Button variant="outline" onClick={() => setEmitOpen(false)}>
              Close
            </Button>
            {!emitResult && !emitError && (
              <Button
                onClick={onEmit}
                loading={andesEmit.isPending}
                disabled={missingSoporte.length > 0}
                variant={emitDryRun ? "primary" : "danger"}
              >
                {emitDryRun ? "Run dry-run" : "Submit (consumes 1 cupo)"}
              </Button>
            )}
          </>
        }
      >
        {!emitResult && !emitError ? (
          andesEmit.isPending ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 rounded border border-slate-200 bg-slate-50 p-3">
                <svg className="h-5 w-5 animate-spin text-brand-600" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <div>
                  <div className="font-medium text-slate-800">
                    {emitDryRun ? "Running dry run…" : "Submitting to Andes…"}
                  </div>
                  <div className="text-xs text-slate-500">
                    {emitDryRun
                      ? "Login, grid fill, soporte ZIP and vigencia config (~1 min)"
                      : "Login, grid fill, soporte, vigencia + Registrar + Acepto + server confirmation. This usually takes 1–3 minutes — please do not close this dialog."}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                The Andes bridger will pull subscriber data + supporting artifacts from this
                system, build the soporte ZIP and drive the panel "Solicitud Masiva" flow.
                Andes rejects requests that ship without the documents below — emission is
                blocked until they are on file.
              </p>
              <ul className="space-y-1 rounded border border-slate-200 bg-slate-50 p-3">
                {emitChecklist.map((item) => (
                  <li key={item.type} className="flex items-center gap-2">
                    <span className={item.ok ? "text-green-700" : "text-red-700"}>
                      {item.ok ? "✓" : "✗"}
                    </span>
                    <span className={item.ok ? "text-slate-700" : "font-medium text-red-800"}>
                      {item.label}
                    </span>
                    {!item.ok && (
                      <span className="text-xs text-red-700">— upload it in the Artifacts tab</span>
                    )}
                  </li>
                ))}
              </ul>
              {missingSoporte.length > 0 && (
                <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900">
                  Cannot emit until the missing soporte is uploaded. This avoids an Andes
                  rejection after the panel is already filled.
                </p>
              )}
              <label className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  checked={emitDryRun}
                  onChange={(e) => setEmitDryRun(e.target.checked)}
                />
                Dry run (stop before "Registrar Solicitudes" — no cupo consumed)
              </label>
              {!emitDryRun && (
                <p className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  ⚠️ Submitting will consume one cupo from the Andes convenio and trigger
                  certificate emission. The full flow takes 1–3 minutes and cannot be cancelled
                  mid-way. Do not close this dialog.
                </p>
              )}
            </div>
          )
        ) : emitError ? (
          <div className="space-y-3 text-sm">
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <div className="font-medium text-red-900">
                [{emitError.code}] {emitError.message}
              </div>
              {emitError.confirmTitle && (
                <div className="mt-1 text-red-800">Diálogo: {emitError.confirmTitle}</div>
              )}
              {emitError.confirmBody && (
                <pre className="mt-1 whitespace-pre-wrap text-xs text-red-800">{emitError.confirmBody}</pre>
              )}
            </div>
            {emitError.attemptId && (
              <Link
                to={`/subscribers/${subscriberId}/andes`}
                className="text-sm font-medium text-brand-700 underline"
                onClick={() => setEmitOpen(false)}
              >
                Saved in Andes tab
              </Link>
            )}
            {emitError.screenshots && emitError.screenshots.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium">Screenshots</div>
                {emitError.screenshots.map((s) => (
                  <figure key={s.name} className="space-y-1">
                    <figcaption className="text-xs text-slate-500">{s.name}</figcaption>
                    <img
                      src={`data:image/png;base64,${s.pngBase64}`}
                      alt={s.name}
                      className="w-full rounded border border-slate-200"
                    />
                  </figure>
                ))}
              </div>
            )}
          </div>
        ) : emitResult ? (
          <div className="space-y-3 text-sm">
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div>
                <span className="font-medium">Submitted:</span>{" "}
                <span className={emitResult.submitted ? "text-green-700" : "text-slate-500"}>
                  {emitResult.submitted ? "yes" : "no (dry run)"}
                </span>
              </div>
              <div>
                <span className="font-medium">Subscriber:</span> {emitResult.subscriberName}
              </div>
              <div>
                <span className="font-medium">Soporte ZIP:</span>{" "}
                {emitResult.soporteZipBytes} bytes ({emitResult.soporteFiles.join(", ")})
              </div>
              <div className="text-slate-600">{emitResult.message}</div>
            </div>
            {emitResult.attemptId && (
              <Link
                to={`/subscribers/${subscriberId}/andes`}
                className="text-sm font-medium text-brand-700 underline"
                onClick={() => setEmitOpen(false)}
              >
                Saved in Andes tab
              </Link>
            )}
            {emitResult.screenshots && emitResult.screenshots.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium">Screenshots</div>
                {emitResult.screenshots.map((s) => (
                  <figure key={s.name} className="space-y-1">
                    <figcaption className="text-xs text-slate-500">{s.name}</figcaption>
                    <img
                      src={`data:image/png;base64,${s.pngBase64}`}
                      alt={s.name}
                      className="w-full rounded border border-slate-200"
                    />
                  </figure>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={pullOpen}
        onClose={() => setPullOpen(false)}
        title="Pull PEM from Andes"
        description={`Documento ${documentNumber} (Persona ${subscriberType})`}
        footer={
          <>
            <Button variant="outline" onClick={() => setPullOpen(false)}>
              Close
            </Button>
            {!pullResult && !pullError && (
              <Button
                onClick={onPullPem}
                loading={andesPullPem.isPending}
                variant={pullDryRun ? "primary" : "danger"}
              >
                {pullDryRun ? "Run dry-run" : "Download PEM and generate PFX/P12"}
              </Button>
            )}
          </>
        }
      >
        {!pullResult && !pullError ? (
          andesPullPem.isPending ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 rounded border border-slate-200 bg-slate-50 p-3">
                <svg className="h-5 w-5 animate-spin text-brand-600" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <div>
                  <div className="font-medium text-slate-800">
                    {pullDryRun ? "Filling Andes identity form…" : "Downloading PEM and generating PFX/P12…"}
                  </div>
                  <div className="text-xs text-slate-500">
                    Parses the emission mail, opens the subscriber panel, then
                    {pullDryRun ? " stops before Validar." : " downloads the PEM and runs Generate PFX + P12."}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                Uses the latest Andes mail <span className="font-medium">Emisión del certificado turno No</span>.
                Persona Jurídica signs in as the legal representative (not the NIT). The one-time
                personal key is re-read from the stored .eml and never saved in D1.
              </p>
              <label className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  checked={pullDryRun}
                  onChange={(e) => setPullDryRun(e.target.checked)}
                />
                Dry run (fill identity form only — no Validar, no PFX)
              </label>
              {!pullDryRun && (
                <p className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  Live pull downloads the issued PEM and generates a new PFX + P12 from the stored
                  private key. KEY_MISMATCH fails the ingest if the cert does not match that key.
                </p>
              )}
            </div>
          )
        ) : pullError ? (
          <div className="space-y-3 text-sm">
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <div className="font-medium text-red-900">
                [{pullError.code}] {pullError.message}
              </div>
            </div>
            {pullError.screenshots && pullError.screenshots.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium">Screenshots</div>
                {pullError.screenshots.map((s) => (
                  <figure key={s.name} className="space-y-1">
                    <figcaption className="text-xs text-slate-500">{s.name}</figcaption>
                    <img
                      src={`data:image/png;base64,${s.pngBase64}`}
                      alt={s.name}
                      className="w-full rounded border border-slate-200"
                    />
                  </figure>
                ))}
              </div>
            )}
          </div>
        ) : pullResult ? (
          <div className="space-y-3 text-sm">
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div>
                <span className="font-medium">Downloaded:</span>{" "}
                {pullResult.downloaded ? "yes" : "no"}
              </div>
              <div>
                <span className="font-medium">Generated:</span>{" "}
                {pullResult.generated ? "PFX + P12" : pullResult.dryRun ? "dry run" : "no"}
              </div>
              {pullResult.turno && (
                <div>
                  <span className="font-medium">Turno:</span> {pullResult.turno}
                </div>
              )}
              {pullResult.serial && (
                <div>
                  <span className="font-medium">Serial:</span> {pullResult.serial}
                </div>
              )}
              <div className="text-slate-600">{pullResult.message}</div>
            </div>
            {pullResult.screenshots && pullResult.screenshots.length > 0 && (
              <div className="space-y-2">
                <div className="font-medium">Screenshots</div>
                {pullResult.screenshots.map((s) => (
                  <figure key={s.name} className="space-y-1">
                    <figcaption className="text-xs text-slate-500">{s.name}</figcaption>
                    <img
                      src={`data:image/png;base64,${s.pngBase64}`}
                      alt={s.name}
                      className="w-full rounded border border-slate-200"
                    />
                  </figure>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </Dialog>
    </Card>
  );
}
